"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

export interface ScaffoldProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional background element (gradient, pattern, etc.) */
  background?: React.ReactNode;
  /** Portal container for modals/toasts/drawers */
  portalContainer?: React.RefObject<HTMLDivElement>;
}

/**
 * Scaffold - Root wrapper for application pages
 *
 * Responsibilities:
 * - Full viewport height management
 * - Global background/gradient application
 * - Portal mounting point for modals/toasts/drawers
 * - Consistent text antialiasing
 */
const Scaffold = React.forwardRef<HTMLDivElement, ScaffoldProps>(
  ({ className, children, background, portalContainer, ...props }, ref) => {
    const portalRef = React.useRef<HTMLDivElement>(null);

    return (
      <div
        ref={ref}
        className={cn(styleDrivers.scaffold.root, className)}
        {...props}
      >
        {background && (
          <div className={styleDrivers.scaffold.background}>{background}</div>
        )}
        {children}
        {/* Portal container for modals, toasts, drawers */}
        <div
          ref={portalContainer || portalRef}
          id="scaffold-portal"
          data-slot="scaffold-portal"
        />
      </div>
    );
  }
);

Scaffold.displayName = "Scaffold";

export { Scaffold };
